import React from "react";
import { IconButton, MenuItem, TextField, Tooltip } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { useQuery } from "@tanstack/react-query";

import GrupoCondiciones from "../filtros/grupo-condiciones";
import {
  FUENTE_PLAN_LECTOR,
  FUENTES_BLOQUE,
  FUENTES_CON_FECHA,
} from "../filtros/filter-registry";
import { GENERAL_URL_API } from "../../../../../shared/urls";

/** Mismo listado que el reporte de lecturas de libros plan lector. */
const fetchInstitutosPlanLector = async () => {
  const fd = new FormData();
  fd.append("show", "all");
  const res = await fetch(`${GENERAL_URL_API}/institutos/verInstitutos.php`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  const data = await res.json();
  if (!Array.isArray(data?.data)) return [];
  return data.data
    .filter(
      (instituto) =>
        typeof instituto?.nombre === "string" && instituto.nombre.length > 0
    )
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
};

/**
 * Un bloque de la recomendación: qué porcentaje del total ocupa, de dónde salen sus
 * libros y qué condiciones los filtran.
 */
const BloqueEditor = ({
  bloque,
  indice,
  total,
  onChange,
  onRemove,
  onMover,
  opcionesApi,
  librosEstimados,
}) => {
  const admiteFechas = FUENTES_CON_FECHA.includes(bloque.fuente);
  const esPlanLector = bloque.fuente === FUENTE_PLAN_LECTOR;
  const etiquetaFecha =
    bloque.fuente === "novedades" ? "Cargados entre" : "Lecturas entre";

  const { data: institutosPlanLector = [], isLoading: cargandoInstitutos } =
    useQuery(["institutos-plan-lector"], fetchInstitutosPlanLector, {
      enabled: esPlanLector,
      staleTime: 5 * 60 * 1000,
    });

  const institutoSeleccionado =
    institutosPlanLector.find(
      (instituto) => instituto.nombre === bloque.institutoPlanLector
    ) ?? null;

  const set = (campo, valor) => onChange({ ...bloque, [campo]: valor });

  return (
    <div className="content-white-box px-3 py-3 mb-3" style={{ border: "1px solid #e0e0e0" }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="admin-main-title-dark-gray">
          Bloque {indice + 1}
          {typeof librosEstimados === "number"
            ? ` — ${librosEstimados} libros`
            : ""}
        </div>
        <div>
          <Tooltip title="Subir">
            <span>
              <IconButton
                size="small"
                disabled={indice === 0}
                onClick={() => onMover(-1)}
              >
                <ArrowUpwardIcon style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Bajar">
            <span>
              <IconButton
                size="small"
                disabled={indice === total - 1}
                onClick={() => onMover(1)}
              >
                <ArrowDownwardIcon style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Eliminar bloque">
            <span>
              <IconButton size="small" disabled={total <= 1} onClick={onRemove}>
                <DeleteIcon style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="d-flex flex-wrap">
        <TextField
          label="Nombre del bloque"
          value={bloque.nombre}
          onChange={(e) => set("nombre", e.target.value)}
          variant="outlined"
          size="small"
          className="mr-2 mb-2"
          style={{ width: "280px", fontFamily: "'Noto Sans JP', sans-serif" }}
        />
        <TextField
          label="Porcentaje"
          value={bloque.porcentaje}
          onChange={(e) => set("porcentaje", e.target.value)}
          variant="outlined"
          size="small"
          type="number"
          inputProps={{ min: 0, max: 100 }}
          className="mr-2 mb-2"
          style={{ width: "120px", fontFamily: "'Noto Sans JP', sans-serif" }}
        />
        <TextField
          select
          label="Fuente"
          value={bloque.fuente}
          onChange={(e) => set("fuente", e.target.value)}
          variant="outlined"
          size="small"
          className="mr-2 mb-2"
          style={{ width: "380px", fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          {FUENTES_BLOQUE.map((fuente) => (
            <MenuItem key={fuente.value} value={fuente.value}>
              {fuente.label}
            </MenuItem>
          ))}
        </TextField>

        {esPlanLector && (
          <Autocomplete
            options={institutosPlanLector}
            loading={cargandoInstitutos}
            getOptionLabel={(option) => option?.nombre ?? ""}
            getOptionSelected={(option, valor) => option?.nombre === valor?.nombre}
            value={institutoSeleccionado}
            onChange={(_, valor) =>
              set("institutoPlanLector", valor?.nombre ?? "")
            }
            noOptionsText="Sin resultados"
            className="mr-2 mb-2"
            style={{ width: "380px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Instituto plan lector"
                variant="outlined"
                size="small"
                placeholder="Selecciona un instituto"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              />
            )}
          />
        )}

        {admiteFechas && (
          <>
            <TextField
              label={etiquetaFecha}
              value={bloque.fechaInicio}
              onChange={(e) => set("fechaInicio", e.target.value)}
              variant="outlined"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              className="mr-2 mb-2"
              style={{ width: "180px", fontFamily: "'Noto Sans JP', sans-serif" }}
            />
            <TextField
              label="Hasta"
              value={bloque.fechaFin}
              onChange={(e) => set("fechaFin", e.target.value)}
              variant="outlined"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              className="mb-2"
              style={{ width: "180px", fontFamily: "'Noto Sans JP', sans-serif" }}
            />
          </>
        )}
      </div>

      {esPlanLector && (
        <div className="admin-muted-text-small mb-2">
          Cuenta las lecturas de todas las sedes de ese instituto plan lector.
        </div>
      )}

      <div className="mt-2">
        <GrupoCondiciones
          condiciones={bloque.condiciones ?? []}
          onChange={(condiciones) => set("condiciones", condiciones)}
          opcionesApi={opcionesApi}
          titulo="Condiciones del bloque"
          descripcion="Se suman a las condiciones globales y al territorio del alcance."
        />
      </div>
    </div>
  );
};

export default BloqueEditor;
